import { useEffect, useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { 
    initializeZone,
    getZoneStatusStart,
    getZoneStatusSuccess,
    getZoneStatusError,
    claimZoneStart,
    claimZoneSuccess,
    claimZoneError,
    updateZonePayloadStart,
    updateZonePayloadSuccess,
    updateZonePayloadError,
    updateZoneStatusStart,
    updateZoneStatusSuccess,
    updateZoneStatusError,
    clearZoneError,
    cleanupExpiredSessions,
    ZONE_IDS,
    type ZoneId,
    type ZoneStatusData
} from '../store/slices/zonesSlice';

// Interface for payload data (simplified from API version)
interface ZonePayloadData {
    payload: any;
}

// Hook for managing individual zone status (Redux-only)
export const useZoneStatus = (zoneId: string, visitorId?: string) => {
    const dispatch = useAppDispatch();
    const { zones, loading, error, claimingZone, lastChecked } = useAppSelector(state => state.zones);
    
    const zoneStatus = zones[zoneId];
    const isLoading = loading[zoneId] || false;
    const zoneError = error[zoneId];
    const isClaiming = claimingZone === zoneId;
    const lastCheck = lastChecked[zoneId];

    // Initialize zone on first access
    useEffect(() => {
        if (!zoneStatus) {
            dispatch(initializeZone({ zoneId }));
        }
    }, [dispatch, zoneId, zoneStatus]);

    // Periodic cleanup of expired sessions
    useEffect(() => {
        const cleanupInterval = setInterval(() => {
            dispatch(cleanupExpiredSessions());
        }, 60000); // Every minute

        return () => clearInterval(cleanupInterval);
    }, [dispatch]);

    // Fetch zone status (Redux-only, simulates API call)
    const fetchZoneStatus = useCallback(() => {
        console.log(`🔍 Fetching zone status for: ${zoneId} (Frontend-only mode)`);
        
        dispatch(getZoneStatusStart({ zoneId }));
        
        // Simulate async operation with minimal delay
        setTimeout(() => {
            dispatch(getZoneStatusSuccess({ zoneId, visitorId }));
        }, 100);
    }, [dispatch, zoneId, visitorId]);

    // Claim zone (Redux-only simulation)
    const claimZone = useCallback(async (claimVisitorId?: string) => {
        const effectiveVisitorId = claimVisitorId || visitorId;
        console.log(`🎯 Claiming zone: ${zoneId} (Frontend-only mode)`, { 
            hasVisitorId: !!effectiveVisitorId,
            visitorIdPreview: effectiveVisitorId ? `${effectiveVisitorId.substring(0, 8)}...` : 'none'
        });

        dispatch(claimZoneStart({ zoneId, visitorId: effectiveVisitorId }));

        return new Promise((resolve) => {
            // Simulate async operation
            setTimeout(() => {
                const currentZone = zones[zoneId];
                
                // Check if zone is available
                if (!currentZone || currentZone.status === 'available') {
                    dispatch(claimZoneSuccess({ zoneId, visitorId: effectiveVisitorId }));
                    resolve({
                        type: 'zones/claimZoneStatus/fulfilled',
                        payload: {
                            zoneId,
                            status: 'processing',
                            claimedBy: effectiveVisitorId || `anon_${Date.now()}`,
                            payload: { option: 0 }
                        }
                    });
                } else {
                    dispatch(claimZoneError({ zoneId, error: 'Zone is currently busy' }));
                    resolve({
                        type: 'zones/claimZoneStatus/rejected',
                        error: { message: 'Zone is currently busy' }
                    });
                }
            }, 200);
        });
    }, [dispatch, zoneId, visitorId, zones]);

    // Update zone status (Redux-only simulation)
    const updateStatus = useCallback(async (statusData: ZoneStatusData) => {
        console.log(`🔄 Updating status for zone: ${zoneId} (Frontend-only mode)`, statusData);
        
        dispatch(updateZoneStatusStart({ zoneId }));

        return new Promise((resolve) => {
            setTimeout(() => {
                dispatch(updateZoneStatusSuccess({ zoneId, statusData, visitorId }));
                resolve({
                    type: 'zones/updateZoneStatus/fulfilled',
                    payload: { ...statusData, zoneId }
                });
            }, 100);
        });
    }, [dispatch, zoneId, visitorId]);

    // Update zone payload (Redux-only simulation)
    const updatePayload = useCallback(async (payloadData: ZonePayloadData) => {
        console.log(`📦 Updating payload for zone: ${zoneId} (Frontend-only mode)`, payloadData);
        
        dispatch(updateZonePayloadStart({ zoneId }));

        return new Promise((resolve) => {
            setTimeout(() => {
                const currentZone = zones[zoneId];
                
                // Check if user has permission
                if (currentZone && (
                    currentZone.claimedBy === visitorId || 
                    (visitorId && currentZone.claimedBy?.startsWith('anon_')) ||
                    (!visitorId && currentZone.claimedBy?.startsWith('anon_'))
                )) {
                    dispatch(updateZonePayloadSuccess({ 
                        zoneId, 
                        payload: payloadData.payload, 
                        visitorId 
                    }));
                    resolve({
                        type: 'zones/updateZonePayload/fulfilled',
                        payload: { 
                            ...currentZone, 
                            payload: { ...currentZone.payload, ...payloadData.payload },
                            zoneId 
                        }
                    });
                } else {
                    dispatch(updateZonePayloadError({ 
                        zoneId, 
                        error: 'Not authorized to update this zone' 
                    }));
                    resolve({
                        type: 'zones/updateZonePayload/rejected',
                        error: { message: 'Not authorized to update this zone' }
                    });
                }
            }, 150);
        });
    }, [dispatch, zoneId, visitorId, zones]);

    // Clear zone error
    const clearError = useCallback(() => {
        if (zoneError) {
            dispatch(clearZoneError(zoneId));
        }
    }, [dispatch, zoneId, zoneError]);

    // Auto-fetch on mount if no data exists or data is stale (older than 30 seconds)
    useEffect(() => {
        const shouldFetch = !zoneStatus || 
                          !lastCheck || 
                          (Date.now() - lastCheck) > 30000; // 30 seconds
        
        if (shouldFetch && !isLoading) {
            fetchZoneStatus();
        }
    }, [zoneId, zoneStatus, lastCheck, isLoading, fetchZoneStatus]);

    return {
        // Zone data
        zoneStatus,
        isLoading,
        error: zoneError,
        isClaiming,
        lastCheck,
        
        // Zone status helpers
        isAvailable: zoneStatus?.status === 'available',
        isProcessing: zoneStatus?.status === 'processing',
        isError: zoneStatus?.status === 'error',
        isClaimedByMe: zoneStatus?.claimedBy === visitorId,
        
        // Actions
        fetchZoneStatus,
        claimZone,
        updateStatus,
        updatePayload,
        clearError,
    };
};

// Hook for managing multiple zones (useful for admin views)
export const useMultipleZones = (zoneIds: string[]) => {
    const dispatch = useAppDispatch();
    const { zones, loading, error } = useAppSelector(state => state.zones);
    
    const fetchAllZones = useCallback(() => {
        console.log(`🔍 Fetching status for multiple zones (Frontend-only mode):`, zoneIds);
        zoneIds.forEach(zoneId => {
            // Initialize zones that don't exist
            if (!zones[zoneId]) {
                dispatch(initializeZone({ zoneId }));
            }
            
            // Fetch status for each zone
            dispatch(getZoneStatusStart({ zoneId }));
            setTimeout(() => {
                dispatch(getZoneStatusSuccess({ zoneId }));
            }, 100);
        });
    }, [dispatch, zoneIds, zones]);

    const zonesData = zoneIds.map(zoneId => ({
        zoneId,
        status: zones[zoneId],
        isLoading: loading[zoneId] || false,
        error: error[zoneId],
    }));

    const isAnyLoading = zoneIds.some(zoneId => loading[zoneId]);
    const hasAnyError = zoneIds.some(zoneId => error[zoneId]);

    return {
        zonesData,
        isAnyLoading,
        hasAnyError,
        fetchAllZones,
    };
};

// Hook specifically for Orang Laut exhibit
export const useOrangLautZone = (visitorId?: string) => {
    return useZoneStatus(ZONE_IDS.ORANG_LAUT, visitorId);
};

// Hook specifically for Market Traders exhibit
export const useMarketTradersZone = (visitorId?: string) => {
    return useZoneStatus(ZONE_IDS.MARKET_TRADERS, visitorId);
};

// Hook specifically for Peranakan Design exhibit
export const usePeranakanDesignZone = (visitorId?: string) => {
    return useZoneStatus(ZONE_IDS.PERANAKAN_DESIGN, visitorId);
}; 